/**
 * Quality tiers (art §3.4) and the auto-tier rule: start at the device default (Medium on coarse pointers, else High);
 * ring buffer of the last 240 rAF deltas; p90 > 20 ms for 120 consecutive frames → drop one tier; p90 < 10 ms for 600
 * frames and below the device default → raise one tier; never during a cinematic or the last 2 s of a fight.
 * Pure (no DOM): the renderer feeds it frame deltas.
 */
import type { QualityTier } from '../shared';
import { PARTICLE_CAPS } from '../content/perf/budgets';
import { QF } from './gpu/layouts';

export const TIERS: readonly QualityTier[] = ['low', 'med', 'high', 'ultra'];

export interface TierSettings {
  tier: QualityTier;
  dprCap: number;
  renderScale: number;
  msaa: 1 | 4;
  sceneFormat: 'rgba8unorm-srgb' | 'rgba16float';
  /** SceneLight.qualityFlags.z feature bits. */
  bits: number;
  furDensity: number;
  hatchLayers: 1 | 2;
  inkBleedTaps: 0 | 2 | 4;
  particleCap: number;
  decalCap: number;
  smear: boolean;
  grain: number;
}

const B = QF;
export const TIER_SETTINGS: Record<QualityTier, TierSettings> = {
  low: {
    tier: 'low', dprCap: 1.5, renderScale: 0.75, msaa: 1, sceneFormat: 'rgba8unorm-srgb', bits: 0, furDensity: 0,
    hatchLayers: 1, inkBleedTaps: 0, particleCap: PARTICLE_CAPS.low[0], decalCap: PARTICLE_CAPS.low[1], smear: false, grain: 0.05,
  },
  med: {
    tier: 'med', dprCap: 2, renderScale: 0.85, msaa: 4, sceneFormat: 'rgba16float',
    bits: B.furStrokes | B.hatch2 | B.sparkle | B.mottling | B.wobble | B.smear, furDensity: 0.6, hatchLayers: 2, inkBleedTaps: 2,
    particleCap: PARTICLE_CAPS.med[0], decalCap: PARTICLE_CAPS.med[1], smear: true, grain: 0.05,
  },
  high: {
    tier: 'high', dprCap: 2, renderScale: 1, msaa: 4, sceneFormat: 'rgba16float',
    bits: B.furStrokes | B.hatch2 | B.hatchGaps | B.sparkle | B.punchwork | B.mottling | B.granulation | B.wobble | B.hueWobble | B.smear,
    furDensity: 1, hatchLayers: 2, inkBleedTaps: 4, particleCap: PARTICLE_CAPS.high[0], decalCap: PARTICLE_CAPS.high[1], smear: true, grain: 0.05,
  },
  ultra: {
    tier: 'ultra', dprCap: 3, renderScale: 1, msaa: 4, sceneFormat: 'rgba16float',
    bits: B.furStrokes | B.hatch2 | B.hatchGaps | B.sparkle | B.punchwork | B.mottling | B.granulation | B.wobble | B.hueWobble | B.smear,
    furDensity: 1, hatchLayers: 2, inkBleedTaps: 4, particleCap: PARTICLE_CAPS.ultra[0], decalCap: PARTICLE_CAPS.ultra[1], smear: true, grain: 0.05,
  },
};

export const isTier = (s: unknown): s is QualityTier => typeof s === 'string' && (TIERS as readonly string[]).includes(s);
export const tierIndex = (t: QualityTier): number => TIERS.indexOf(t);

/** Device default (art §3.4): Medium on coarse pointers (phones, tablets), else High. */
export function deviceDefaultTier(coarsePointer: boolean): QualityTier {
  return coarsePointer ? 'med' : 'high';
}

/** Internal pixel size: css × min(dpr, cap) × scale. */
export function internalSize(cssW: number, cssH: number, dpr: number, t: TierSettings): { canvasW: number; canvasH: number; sceneW: number; sceneH: number; pxPerCss: number } {
  const d = Math.min(dpr, t.dprCap);
  const canvasW = Math.max(1, Math.round(cssW * d));
  const canvasH = Math.max(1, Math.round(cssH * d));
  return { canvasW, canvasH, sceneW: Math.max(1, Math.round(canvasW * t.renderScale)), sceneH: Math.max(1, Math.round(canvasH * t.renderScale)), pxPerCss: d };
}

/** The auto-tier controller (pure; unit-tested by tests/engine/quality.test.ts). */
export class AutoTier {
  private readonly ring = new Float32Array(240);
  private readonly sorted = new Float32Array(240);
  private n = 0;
  private head = 0;
  private slowRun = 0;
  private fastRun = 0;
  private fpsCap: number | null = null;
  tier: QualityTier;
  /** Set by the director during a cinematic or the last 2 s of a fight. */
  locked = false;

  constructor(readonly deviceDefault: QualityTier, start: QualityTier = deviceDefault) {
    this.tier = start;
  }

  /** p90 of the ring buffer. */
  p90(): number {
    if (this.n === 0) return 0;
    this.sorted.set(this.ring);
    for (let i = this.n; i < this.sorted.length; i++) this.sorted[i] = Infinity; // no subarray: zero allocation
    this.sorted.sort();
    return this.sorted[Math.min(this.n - 1, Math.floor(this.n * 0.9))];
  }

  /** Feeds one rAF delta; returns the new tier when it changes, else null. */
  push(dtMs: number, fpsCap: number | null = null): QualityTier | null {
    const cap=fpsCap!==null&&fpsCap>0&&fpsCap<60?fpsCap:null;
    // Battery saving and the user's 30 fps ceiling are intentional cadence,
    // not evidence that the device cannot render the current detail level.
    if(cap!==this.fpsCap){this.fpsCap=cap;this.reset();}
    this.ring[this.head] = dtMs*(cap===null?1:cap/60);
    this.head = (this.head + 1) % this.ring.length;
    this.n = Math.min(this.n + 1, this.ring.length);
    const p = this.p90();
    this.slowRun = p > 20 ? this.slowRun + 1 : 0;
    this.fastRun = p < 10 ? this.fastRun + 1 : 0;
    if (this.locked) return null;
    const i = tierIndex(this.tier);
    if (this.slowRun >= 120 && i > 0) {
      this.tier = TIERS[i - 1];
      this.reset();
      return this.tier;
    }
    if (this.fastRun >= 600 && i < tierIndex(this.deviceDefault)) {
      this.tier = TIERS[i + 1];
      this.reset();
      return this.tier;
    }
    return null;
  }

  private reset(): void {
    this.n = 0; this.head = 0; this.slowRun = 0; this.fastRun = 0;
  }
}
